import type { Invoice, Status } from "./models";

interface PaymentState {
  paying: boolean;
  payResult: string | null;
  payError: string | null;
  payResults: PaymentLogEntry[] | null;
  pendingInvoices: Invoice[];
  paymentStatus: PaymentFlowStatus;
  processedCount: number;
  paymentLogs: PaymentLog[];
  paymentLogsLoading: boolean;
  paymentLogsError: string | null;
  currentPaymentLogId: number | null;
  logEntries: LogMessage[];
  twoFaPrompt: Prompt2faCallback | null;
  twoFaCode: string;
}

type PaymentPatch = {
  [K in keyof PaymentState]?: PaymentState[K] | ((prev: PaymentState[K]) => PaymentState[K]);
};

enum PaymentResultStatus {
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

enum PaymentStatus {
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELLED = "CANCELLED",
}

enum PaymentSource {
  FE = "FE",
  MOBILE = "MOBILE",
  DISCORD = "DISCORD",
}

enum SortColumn {
  INVOICE_DATE = "invoiceDate",
  CREATED_AT = "createdAt",
  AMOUNT = "amount",
}

enum SortDirection {
  ASC = "asc",
  DESC = "desc",
}

interface PaymentResult {
  invoiceId: number;
  address: string;
  provider: string;
  amount: number;
  invoiceDate: string;
  success: boolean;
}

interface PayInvoiceResponse {
  paymentLogId: number;
  results?: PaymentResult[];
  message?: string;
}

interface SortConfig {
  column: SortColumn | undefined;
  direction: SortDirection | undefined;
}

interface SortOption {
  label: string;
  sortLabel: string;
}

interface PaginatedInvoiceListParams {
  page: number;
  limit: number;
  status?: Status | Status[];
  providerId?: number | number[];
  addressId?: number | number[];
  dateFrom?: string;
  dateTo?: string;
  sortBy?: SortColumn;
  sortOrder?: SortDirection;
  [key: string]: unknown;
}

interface PaymentLogMessage {
  id: number;
  paymentLogId: number;
  key?: string;
  params?: LogParams;
  createdAt: string;
}

interface PaymentLogEntry {
  id: number;
  paymentLogId: number;
  invoiceId: number | null;
  addressName: string;
  providerName: string;
  amount: number;
  invoiceDate: string;
  success: PaymentResultStatus;
  createdAt: string;
  messages: PaymentLogMessage[];
}

interface PaymentLog {
  id: number;
  userId: number | null;
  runAt: string;
  status: PaymentStatus;
  source: PaymentSource;
  totalInvoices: number;
  completedAt: string | null;
  errorMessage: string | null;
  simulated: boolean;
  entries: PaymentLogEntry[];
  messages: PaymentLogMessage[];
}

interface InvoicePaymentRun {
  id: number;
  runAt: string;
  status: PaymentStatus;
  source: PaymentSource;
  errorMessage: string | null;
  completedAt: string | null;
}

interface InvoicePaymentAttempt {
  id: number;
  paymentLogId: number;
  invoiceId: number | null;
  addressName: string;
  providerName: string;
  amount: number;
  invoiceDate: string;
  success: PaymentResultStatus;
  createdAt: string;
  paymentLog: InvoicePaymentRun;
}

interface InvoiceNeighbor {
  id: number;
  invoiceDate: string;
  amount: number | null;
  status: Status;
}

type InvoiceNeighborKey = "previousInvoice" | "nextInvoice";

type InvoiceAverageComparison = "above" | "matches" | "below";

interface InvoiceDetail extends Invoice {
  paymentHistory: InvoicePaymentAttempt[];
  previousInvoice: InvoiceNeighbor | null;
  nextInvoice: InvoiceNeighbor | null;
  providerAverageAmount: number | null;
  addressAverageAmount: number | null;
}

interface PaymentProgressResult {
  invoiceId: number | null;
  addressName: string;
  providerName: string;
  amount: number;
  invoiceDate: string;
  success: PaymentResultStatus;
}

type LogParams = Record<string, string | number>;

interface PaymentProgressEvent {
  type: "progress";
  result: PaymentProgressResult;
}

interface PaymentDoneEvent {
  type: "done";
  message: string;
  key?: string;
  params?: LogParams;
  results?: PaymentProgressResult[];
}

interface PaymentErrorEvent {
  type: "error";
  message?: string;
  key?: string;
  params?: LogParams;
}

interface PaymentPrompt2faEvent {
  type: "prompt_2fa";
  requestId: string;
  message: string;
}

interface PaymentDismiss2faEvent {
  type: "dismiss_2fa";
}

interface RealTimeLogEvent {
  type: "log";
  key?: string;
  params?: LogParams;
  timestamp?: number;
}

type PaymentEvent = PaymentProgressEvent | PaymentDoneEvent | PaymentErrorEvent | PaymentPrompt2faEvent | PaymentDismiss2faEvent | RealTimeLogEvent;

enum PaymentFlowStatus {
  IDLE = "idle",
  PROCESSING = "processing",
  DONE = "done",
  ERROR = "error",
}

type Prompt2faCallback = Omit<PaymentPrompt2faEvent, "type">;
type RealTimeLogCallback = Omit<RealTimeLogEvent, "type">;

interface JobLogCallback {
  jobId: string;
  status: string;
  message: string;
}

interface LogMessage {
  key?: string;
  params?: LogParams;
  timestamp: string;
  ts?: number;
}

interface PaymentHandlerMessages {
  noInvoices: string;
  paymentFailed: string;
}

interface PaymentInitHandlers {
  onSocketDisconnect?: () => void;
  onConnectToPayment?: (paymentLogId: number) => void;
  onLogReload?: () => void;
  messages?: PaymentHandlerMessages;
}

export {
  type InvoiceAverageComparison,
  type InvoiceDetail,
  type InvoiceNeighbor,
  type InvoiceNeighborKey,
  type InvoicePaymentAttempt,
  type InvoicePaymentRun,
  type JobLogCallback,
  type LogMessage,
  type LogParams,
  type PaginatedInvoiceListParams,
  type PayInvoiceResponse,
  type PaymentDismiss2faEvent,
  type PaymentDoneEvent,
  type PaymentErrorEvent,
  type PaymentEvent,
  PaymentFlowStatus,
  type PaymentHandlerMessages,
  type PaymentInitHandlers,
  type PaymentLog,
  type PaymentLogEntry,
  type PaymentLogMessage,
  type PaymentPatch,
  type PaymentProgressEvent,
  type PaymentProgressResult,
  type PaymentPrompt2faEvent,
  type PaymentResult,
  PaymentResultStatus,
  PaymentSource,
  type PaymentState,
  PaymentStatus,
  type Prompt2faCallback,
  type RealTimeLogCallback,
  type RealTimeLogEvent,
  SortColumn,
  type SortConfig,
  SortDirection,
  type SortOption,
};
